import { useState, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  RIASEC_FIELDS_MAP,
  RIASEC_QUESTIONS,
  RiasecCode,
} from "../../Data/mockDataCareerTest";
import { useAuth } from "../../context/AuthContext";
import TestNav from "../../Components/CareerTest/CareerTestNav";
import IntroScreen from "../../Components/CareerTest/IntroScreen";
import ProgressBar from "../../Components/ProgressBar";
import QuestionCard from "../../Components/CareerTest/QuestionCard";
import LoadingScreen from "../../Components/CareerTest/LoadingScreen";
import ResultsScreen from "../../Components/CareerTest/ResultsScreen";
// ── Scoring ───────────────────────────────────────────────────────────────────

function computeScores(
  answers: (0 | 1 | 2 | null)[],
): Record<RiasecCode, number> {
  const s: Record<RiasecCode, number> = { R: 0, I: 0, A: 0, S: 0, E: 0, C: 0 };
  RIASEC_QUESTIONS.forEach((q, i) => {
    s[q.type] += answers[i] ?? 0;
  });
  return s;
}

function getTopTypes(scores: Record<RiasecCode, number>): RiasecCode[] {
  return (Object.keys(scores) as RiasecCode[])
    .sort((a, b) => scores[b] - scores[a])
    .slice(0, 3);
}

function getCareerRecommendations(topTypes: RiasecCode[]) {
  return {
    fields: [...new Set(topTypes.flatMap((c) => RIASEC_FIELDS_MAP[c].fields))],
    courses: [
      ...new Set(topTypes.flatMap((c) => RIASEC_FIELDS_MAP[c].courses)),
    ],
    careers: [
      ...new Set(topTypes.flatMap((c) => RIASEC_FIELDS_MAP[c].careers)),
    ],
  };
}

// ── Page ──────────────────────────────────────────────────────────────────────

type Phase = "intro" | "quiz" | "loading" | "results";

export default function CareerTestPage() {
  const { saveCareerTestResult, isAuthenticated } = useAuth();
  const [phase, setPhase] = useState<Phase>("intro");
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState<(0 | 1 | 2 | null)[]>(
    Array(30).fill(null),
  );
  const [scores, setScores] = useState<Record<RiasecCode, number> | null>(null);
  const [topTypes, setTopTypes] = useState<RiasecCode[]>([]);
  const [direction, setDirection] = useState<"forward" | "back">("forward");
  const [saved, setSaved] = useState(false);

  const total = RIASEC_QUESTIONS.length;

  const handleStart = () => setPhase("quiz");

  const handleAnswer = (value: 0 | 1 | 2) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[currentQ] = value;
      return next;
    });
  };

  const handleNext = async () => {
    if (currentQ < total - 1) {
      setDirection("forward");
      setCurrentQ((q) => q + 1);
    } else {
      // Last question — compute and show loading
      const s = computeScores(answers);
      const top = getTopTypes(s);
      setScores(s);
      setTopTypes(top);
      const success = await saveCareerTestResult({
        scores: s as Record<string, number>,
        topTypes: top,
        recommendations: getCareerRecommendations(top),
        completedAt: new Date().toISOString(),
      });
      if (success && isAuthenticated) {
        setSaved(true);
      }
      setPhase("loading");
      setTimeout(() => setPhase("results"), 2800);
    }
  };

  const handleBack = () => {
    if (currentQ === 0) {
      setPhase("intro");
    } else {
      setDirection("back");
      setCurrentQ((q) => q - 1);
    }
  };

  const handleRetake = () => {
    setPhase("intro");
    setCurrentQ(0);
    setAnswers(Array(30).fill(null));
    setScores(null);
    setTopTypes([]);
    setSaved(false);
  };

  const handleSave = async () => {
    if (!scores) return;
    const success = await saveCareerTestResult({
      scores: scores as Record<string, number>,
      topTypes,
      recommendations: getCareerRecommendations(topTypes),
      completedAt: new Date().toISOString(),
    });
    if (success) {
      setSaved(true);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#F5F5F8]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <TestNav />

      <main className="pt-24 pb-16 px-4">
        <AnimatePresence mode="wait">
          {phase === "intro" && (
            <motion.div
              key="intro"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <IntroScreen onStart={handleStart} />
            </motion.div>
          )}

          {phase === "quiz" && (
            <motion.div
              key="quiz"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <ProgressBar current={currentQ + 1} total={total} />
              <QuestionCard
                question={RIASEC_QUESTIONS[currentQ]}
                index={currentQ}
                total={total}
                answer={answers[currentQ]}
                onAnswer={handleAnswer}
                onNext={handleNext}
                onBack={handleBack}
                direction={direction}
              />
            </motion.div>
          )}

          {phase === "loading" && (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <LoadingScreen />
            </motion.div>
          )}

          {phase === "results" && scores && (
            <motion.div
              key="results"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <ResultsScreen
                scores={scores}
                topTypes={topTypes}
                onRetake={handleRetake}
                onSave={handleSave}
                saved={saved}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
